import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://gtbuyspreadsheet.run";
const logoUrl = "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/54d75be42d5913aa14ca64dd22e5d1e3.jpeg";

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "Why Ecommerce Sellers Use Spreadsheet Sourcing Systems",
  description: "The top reasons successful online store owners switch from scattered supplier management to structured spreadsheet methodologies for better decisions and faster growth.",
  datePublished: "2026-07-01",
  dateModified: "2026-07-01",
  author: {
    "@type": "Organization",
    name: "GTBuy Spreadsheet",
    url: siteUrl,
  },
  publisher: {
    "@type": "Organization",
    name: "GTBuy Spreadsheet",
    logo: {
      "@type": "ImageObject",
      url: logoUrl,
    },
  },
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": `${siteUrl}/why-use-spreadsheet-sourcing`,
  },
};

export default function WhyUseSpreadsheetSourcing() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <Navbar />
      <main>
        <section className="w-full pt-24 pb-12 md:pt-32 md:pb-16 px-6 md:px-10 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-5">
              <Link to="/" className="text-sm text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer flex items-center gap-1"><i className="ri-arrow-left-line"></i> Back to Home</Link>
              <span className="text-foreground-300">/</span>
              <span className="text-sm text-foreground-500">Insights</span>
            </div>
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-4">Insights &middot; 5 min read</span>
            <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 leading-tight mb-4">Why Ecommerce Sellers Use Spreadsheet Sourcing Systems</h1>
            <p className="text-base text-foreground-600 leading-relaxed max-w-2xl">The top reasons successful online store owners are switching from scattered supplier management to structured spreadsheet methodologies. Discover why organized data drives better decisions and faster growth.</p>
            <p className="text-xs text-foreground-400 mt-4">Published: July 2026</p>
          </div>
        </section>

        <section className="w-full px-6 md:px-10 pb-10">
          <div className="max-w-4xl mx-auto">
            <div className="aspect-[16/7] w-full rounded-2xl overflow-hidden bg-background-200">
              <img src="https://readdy.ai/api/search-image?query=Well-organized%20home%20office%20with%20large%20ultrawide%20monitor%20displaying%20detailed%20spreadsheet%20with%20organized%20product%20data%20tables%20colorful%20charts%20to%20the%20side%20minimalist%20desk%20with%20succulent%20plant%20and%20desk%20lamp%20bright%20natural%20daylight%20clean%20productive%20atmosphere%20editorial%20photography%20high%20detail&width=1600&height=700&seq=article-why-spreadsheet-header&orientation=landscape"
                alt="Spreadsheet Sourcing Systems" title="Why Ecommerce Sellers Use Spreadsheet Sourcing" className="w-full h-full object-cover" loading="eager" />
            </div>
          </div>
        </section>

        <article className="w-full px-6 md:px-10 pb-16">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-10 mb-4">The Hidden Cost of Disorganized Sourcing</h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">Every ecommerce seller starts the same way: a few products, a couple of suppliers, and an email inbox that somehow manages it all. But as the catalog grows, <strong>disorganization becomes expensive</strong>. Missed reorder windows, forgotten price comparisons, and duplicate supplier research silently eat into margins.</p>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">A structured spreadsheet system transforms sourcing from a reactive scramble into a <strong>proactive competitive advantage</strong>. Here are the five reasons sellers are making the switch.</p>

            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-12 mb-4">Top 5 Reasons Sellers Use Spreadsheets</h2>
            <ol className="list-decimal list-inside space-y-5 mb-8 text-base text-foreground-700 leading-relaxed">
              <li><strong>Complete data ownership.</strong> Unlike platform dashboards that limit what you can see and export, a spreadsheet gives you unrestricted access to your own data. You can build any custom view or calculation you need.</li>
              <li><strong>Cross-supplier comparison.</strong> Spreadsheets let you compare prices, lead times, and quality scores across multiple suppliers in a single view — something most sourcing platforms simply do not support.</li>
              <li><strong>Historical tracking.</strong> When you track supplier prices over months and years, patterns emerge. You learn when to buy, which suppliers consistently offer the best value, and when to renegotiate.</li>
              <li><strong>Team collaboration.</strong> A shared spreadsheet becomes the central reference point for your entire team. New team members onboard faster, and everyone works from the same accurate data.</li>
              <li><strong>Platform independence.</strong> Your sourcing data should not be trapped inside any single platform. Spreadsheets export to any format and integrate with any tool you might use in the future.</li>
            </ol>

            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-12 mb-4">Getting Started is Simpler Than You Think</h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">You do not need to build a complex system from day one. Start with a <strong>single sheet containing your top 20 products and their best three supplier quotes</strong>. As you use this daily, you will naturally identify what additional columns and sheets would add value. The key is starting — the structure evolves organically as your business grows.</p>

            <p className="text-sm text-foreground-500 italic mb-4">
              Explore further:{" "}
              <Link to="/dropshipping-sourcing-system" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">Dropshipping Sourcing System</Link>{" "}&middot;{" "}
              <Link to="/how-to-use-gtbuy" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">How to Use GTBuy</Link>{" "}&middot;{" "}
              <Link to="/" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">Homepage</Link>
            </p>
          </div>
        </article>

        <section className="relative w-full py-20 px-6 md:px-10 bg-foreground-950 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-background-50 mb-4">Organize Your Sourcing Today</h2>
            <p className="text-foreground-400 text-sm mb-6">Browse 14 product categories on KA Spreadsheet and bring structured sourcing to your ecommerce workflow.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">GTBuy Sheet — Shop Now</a>
              <Link to="/" className="px-6 py-3 rounded-full bg-foreground-800 text-background-50 font-medium text-sm hover:bg-foreground-700 transition-colors cursor-pointer whitespace-nowrap">Back to Homepage</Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}